import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';

const GOALS_OPTIONS = [
  { id: 'retirement', label: 'Save for retirement', emoji: '🏖️' },
  { id: 'debt',       label: 'Pay off debt',         emoji: '💳' },
  { id: 'emergency',  label: 'Build emergency fund',  emoji: '🛡️' },
  { id: 'home',       label: 'Buy a home',            emoji: '🏡' },
  { id: 'invest',     label: 'Invest more',           emoji: '📈' },
  { id: 'tracking',   label: 'Track spending',        emoji: '📊' },
  { id: 'education',  label: 'Save for education',    emoji: '🎓' },
  { id: 'other',      label: 'Other',                 emoji: '✨' },
];

const INCOME_OPTIONS = [
  { id: 'under50',   label: 'Under $50,000' },
  { id: '50to100',   label: '$50,000 – $100,000' },
  { id: '100to200',  label: '$100,000 – $200,000' },
  { id: 'over200',   label: '$200,000+' },
];

const RISK_OPTIONS = [
  {
    id: 'conservative',
    label: 'Conservative',
    desc: "I prefer safety. Lower returns are fine if it means minimal risk.",
    color: 'border-emerald-600/60 bg-emerald-900/20',
    activeColor: 'border-emerald-400 bg-emerald-900/40',
    emoji: '🛡️',
  },
  {
    id: 'moderate',
    label: 'Moderate',
    desc: 'Balanced approach — some growth potential with manageable risk.',
    color: 'border-blue-600/60 bg-blue-900/20',
    activeColor: 'border-blue-400 bg-blue-900/40',
    emoji: '⚖️',
  },
  {
    id: 'aggressive',
    label: 'Aggressive',
    desc: 'I seek maximum growth and can handle significant market swings.',
    color: 'border-purple-600/60 bg-purple-900/20',
    activeColor: 'border-purple-400 bg-purple-900/40',
    emoji: '🚀',
  },
];

export default function WelcomeSurvey({ onComplete, onSkip }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    goals: [],
    incomeRange: null,
    riskTolerance: null,
  });

  const toggleGoal = (id) => {
    setAnswers(a => ({
      ...a,
      goals: a.goals.includes(id) ? a.goals.filter(g => g !== id) : [...a.goals, id],
    }));
  };

  const canNext = () => {
    if (step === 0) return answers.goals.length > 0;
    if (step === 1) return answers.incomeRange !== null;
    if (step === 2) return answers.riskTolerance !== null;
    return true;
  };

  const handleNext = () => {
    if (step < 2) {
      setStep(s => s + 1);
    } else {
      onComplete(answers);
    }
  };

  const STEPS_LABELS = ['Your Goals', 'Income Range', 'Risk Tolerance'];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Progress */}
        <div className="flex gap-1 mb-4">
          {STEPS_LABELS.map((label, i) => (
            <div key={i} className="flex-1">
              <div className={`h-1 rounded-full transition-all ${i <= step ? 'bg-blue-500' : 'bg-slate-700'}`} />
              <div className={`text-xs mt-1 text-center transition-colors ${i === step ? 'text-blue-400' : 'text-slate-600'}`}>
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* Card */}
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
          {/* Step 0: Goals */}
          {step === 0 && (
            <>
              <h2 className="text-xl font-bold text-slate-100 mb-1">What are your financial goals?</h2>
              <p className="text-slate-400 text-sm mb-5">Select all that apply — we'll personalize your dashboard.</p>
              <div className="grid grid-cols-2 gap-2">
                {GOALS_OPTIONS.map(opt => {
                  const selected = answers.goals.includes(opt.id);
                  return (
                    <button
                      key={opt.id}
                      onClick={() => toggleGoal(opt.id)}
                      className={`flex items-center gap-2.5 p-3 rounded-xl border text-left transition-all ${
                        selected
                          ? 'border-blue-500 bg-blue-900/30 text-slate-100'
                          : 'border-slate-700 bg-slate-900/50 text-slate-400 hover:border-slate-600 hover:text-slate-300'
                      }`}
                    >
                      <span className="text-lg flex-shrink-0">{opt.emoji}</span>
                      <span className="text-xs font-medium leading-tight">{opt.label}</span>
                      {selected && <CheckCircle size={13} className="text-blue-400 ml-auto flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Step 1: Income */}
          {step === 1 && (
            <>
              <h2 className="text-xl font-bold text-slate-100 mb-1">What's your approximate annual income?</h2>
              <p className="text-slate-400 text-sm mb-5">This helps us calibrate budget recommendations.</p>
              <div className="space-y-2">
                {INCOME_OPTIONS.map(opt => {
                  const selected = answers.incomeRange === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => setAnswers(a => ({ ...a, incomeRange: opt.id }))}
                      className={`w-full flex items-center justify-between p-4 rounded-xl border text-left transition-all ${
                        selected
                          ? 'border-blue-500 bg-blue-900/30 text-slate-100'
                          : 'border-slate-700 bg-slate-900/50 text-slate-400 hover:border-slate-600 hover:text-slate-300'
                      }`}
                    >
                      <span className="text-sm font-medium">{opt.label}</span>
                      {selected && <CheckCircle size={16} className="text-blue-400" />}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Step 2: Risk tolerance */}
          {step === 2 && (
            <>
              <h2 className="text-xl font-bold text-slate-100 mb-1">What's your risk tolerance?</h2>
              <p className="text-slate-400 text-sm mb-5">We'll use this to personalize investing & retirement advice.</p>
              <div className="space-y-3">
                {RISK_OPTIONS.map(opt => {
                  const selected = answers.riskTolerance === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => setAnswers(a => ({ ...a, riskTolerance: opt.id }))}
                      className={`w-full flex items-start gap-4 p-4 rounded-xl border text-left transition-all ${
                        selected ? opt.activeColor : opt.color
                      } hover:opacity-90`}
                    >
                      <span className="text-2xl flex-shrink-0">{opt.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-semibold mb-0.5 ${selected ? 'text-slate-100' : 'text-slate-300'}`}>
                          {opt.label}
                        </div>
                        <div className="text-xs text-slate-400 leading-snug">{opt.desc}</div>
                      </div>
                      {selected && <CheckCircle size={16} className="text-blue-400 flex-shrink-0 mt-0.5" />}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <button
              onClick={step === 0 ? onSkip : () => setStep(s => s - 1)}
              className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 transition-colors"
            >
              {step === 0 ? (
                'Skip survey'
              ) : (
                <><ArrowLeft size={14} /> Back</>
              )}
            </button>

            <button
              onClick={handleNext}
              disabled={!canNext()}
              className={`flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                canNext()
                  ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/40'
                  : 'bg-slate-700 text-slate-500 cursor-not-allowed'
              }`}
            >
              {step === 2 ? 'Finish' : 'Next'}
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
